import { Color3, Curve3, Mesh, MeshBuilder, Scene, Vector3 } from '@babylonjs/core';
import { mat } from './arena';
import type { LineArena } from '../data/arenas';

export interface LineTrack {
  /** Densely-sampled Catmull-Rom path through the arena's waypoints, start → finish. */
  path: Vector3[];
  ground: Mesh;
  ribbon: Mesh;
  startMarker: Mesh;
  finishMarker: Mesh;
}

/**
 * Builds the white floor + black line + start/finish markers for a Line Follower arena.
 * Shared by Compete's own Simulate tab and the kit-assembly workbench, so track visuals
 * only need to be built (and fixed) in one place.
 */
export function buildLineTrack(scene: Scene, arena: LineArena, lineWidthCm: number): LineTrack {
  const ground = MeshBuilder.CreateGround('lground', { width: 6, height: 5 }, scene);
  ground.material = mat(scene, 'lground-mat', new Color3(1, 1, 1));

  const waypoints = arena.waypoints.map(([x, z]) => new Vector3(x, 0, z));
  const path = Curve3.CreateCatmullRomSpline(waypoints, 16, false).getPoints();

  const lineW = lineWidthCm / 100;
  const left: Vector3[] = [];
  const right: Vector3[] = [];
  for (let i = 0; i < path.length; i++) {
    const p = path[i];
    const q = path[Math.min(i + 1, path.length - 1)];
    const tan = q.subtract(path[Math.max(i - 1, 0)]).normalize();
    const n = new Vector3(-tan.z, 0, tan.x).scale(lineW * 2); // drawn wider for visibility
    left.push(new Vector3(p.x + n.x, 0.005, p.z + n.z));
    right.push(new Vector3(p.x - n.x, 0.005, p.z - n.z));
  }
  const ribbon = MeshBuilder.CreateRibbon('line', { pathArray: [left, right] }, scene);
  ribbon.material = mat(scene, 'line-mat', new Color3(0, 0, 0));

  const startMarker = MeshBuilder.CreateGround('start', { width: 0.3, height: 0.08 }, scene);
  startMarker.position = path[0].clone();
  startMarker.position.y = 0.006;
  startMarker.material = mat(scene, 'start-mat', new Color3(0.1, 0.8, 0.3), 0.5);

  const finishMarker = MeshBuilder.CreateGround('finish', { width: 0.3, height: 0.08 }, scene);
  finishMarker.position = path[path.length - 1].clone();
  finishMarker.position.y = 0.006;
  finishMarker.material = mat(scene, 'finish-mat', new Color3(0.9, 0.2, 0.2), 0.5);

  return { path, ground, ribbon, startMarker, finishMarker };
}
